import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState, useMemo } from "react";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FileUpload from "../../../../../../components/FileUpload";
import { useClaimFormStore } from "../../../../../../store/useClaimFormStore";
import { useUserStore } from "../../../../../../store/useUserStore";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { getDocument } from "../../../../../../services/dashboard";

const DOC_KEY_MAP: Record<string, string> = {
  ASSESSMENT_DOCUMENT: "assessment_document",
  VERIFICATION_DOCUMENT: "verification_document",
  FRAUD_DOCUMENTATION: "fraud_documentation",
  CLAIMS_REPORT: "claims_report",
  OTHER_DOCUMENT: "other_document",
};

export default function UnderwriterReview({
  isAssigned,
  claimStatus,
  isEditable,
  reviewComment,
  onReviewCommentChange,
  documentData: initialDocumentData,
  onFileChosen,
  editMode,
  commentData,
  iceClaimNumber,
  setDeletedDocs,
}: any) {
  const [documents, setDocuments] = useState<Record<string, any[]>>({});
  const [fileErrors, setFileErrors] = useState<Record<string, string>>({});
  const [openPreview, setOpenPreview] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<any>(null);

  const addDocument = useClaimFormStore((s) => s.addDocument);
  const removeDocument = useClaimFormStore((s) => s.removeDocument);
  const documentsFromStore = useClaimFormStore((s) => s.documents);
  const role = useUserStore((s) => s.role);

  const canUpload = role === "underwriter";

  const comments = commentData?.filter(
    (comment: any) => comment.section_type === "UNDERWRITER_REVIEW",
  );

  const formatDate = (dateString: string) => {
    const d = new Date(dateString);
    return `${String(d.getDate()).padStart(2, "0")}-${String(
      d.getMonth() + 1,
    ).padStart(2, "0")}-${d.getFullYear()}`;
  };

  useEffect(() => {
    const grouped = initialDocumentData.reduce(
      (acc: Record<string, any[]>, doc: any) => {
        (acc[doc.document_type] ??= []).push(doc);
        return acc;
      },
      {},
    );

    const underwriterDocType = [
      "ASSESSMENT_DOCUMENT",
      "VERIFICATION_DOCUMENT",
      "FRAUD_DOCUMENTATION",
      "CLAIMS_REPORT",
      "OTHER_DOCUMENT",
    ];

    const mergedDocuments: Record<string, any[]> = { ...grouped };

    Object.entries(documentsFromStore).forEach(([type, file]) => {
      const key = type.toUpperCase();
      if (underwriterDocType.includes(key)) {
        mergedDocuments[key] = [
          {
            file_name: file.name,
            file_url: URL.createObjectURL(file),
            isLocal: true,
          },
        ];
      }
    });

    setDocuments(mergedDocuments);
  }, []);

  // ------ helper flags ------
  const anyDocsUploaded = useMemo(
    () =>
      Object.values(documents).some(
        (arr) => Array.isArray(arr) && arr.length > 0,
      ),
    [documents],
  );

  const handleDelete = (type: string, index: number) => {
    let removedDoc: any = null;

    setDocuments((prev: any) => {
      const updated = [...(prev[type] || [])];
      [removedDoc] = updated.splice(index, 1);

      return { ...prev, [type]: updated };
    });
    if (removedDoc && !removedDoc.isLocal && removedDoc.document_id) {
      setDeletedDocs((prev: any[]) =>
        prev.includes(removedDoc.document_id)
          ? prev
          : [...prev, removedDoc.document_id],
      );
    }

    const storeKey = DOC_KEY_MAP[type];
    if (storeKey) {
      removeDocument(storeKey);
    }
  };

  const handlePreview = async (doc: any) => {
    try {
      if (doc.isLocal) {
        setSelectedDoc(doc);
        setOpenPreview(true);
      } else {
        const res = await getDocument(iceClaimNumber, doc?.document_id);
        if (!res || res.status !== 200) {
          throw new Error("Failed to fetch document");
        }

        const blob = res.data;
        const url = URL.createObjectURL(blob);
        setSelectedDoc({ ...doc, file_url: url });
        setOpenPreview(true);
      }
    } catch (err) {
      console.error("Error fetching file:", err);
    }
  };

  const handleDownload = async (doc: any) => {
    try {
      const res = await getDocument(iceClaimNumber, doc.document_id);
      if (!res || res.status !== 200) {
        throw new Error("Failed to download document");
      }

      const blob = res.data;
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = doc.file_name || "document.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading file:", err);
    }
  };

  const handleClosePreview = () => {
    setOpenPreview(false);
    setSelectedDoc(null);
  };

  if (
    !anyDocsUploaded &&
    (role === "finance" ||
      role === "consultant" ||
      role === "claim_administrator")
  ) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="body1" color="text.secondary">
          No data to display
        </Typography>
        {comments?.length > 0 && (
          <Grid size={12} mt={3}>
            {comments.map((comment: any, index: number) => (
              <Accordion
                key={index}
                sx={{
                  backgroundColor: "#F7F7F7",
                  boxShadow: "none",
                  borderRadius: "6px",
                  mb: 2,
                  border: "1px solid #E0E0E0",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`panel${index}-content`}
                  id={`panel${index}-header`}
                  sx={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography fontWeight={600} fontSize={14}>
                      {comment.commentedByName}{" "}
                      <Typography
                        component="span"
                        fontWeight={600}
                        color="#EC6E00"
                      >
                        - {comment.role_at_time_of_comment?.toUpperCase()}
                      </Typography>
                    </Typography>
                    <Typography fontSize={12} color="text.secondary">
                      {new Date(comment.created_on).toLocaleTimeString(
                        "en-US",
                        {
                          hour: "numeric",
                          minute: "2-digit",
                        },
                      )}{" "}
                      | {formatDate(comment.created_on)}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography fontSize={15} color="#333" lineHeight={1.6}>
                    {comment.content}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Grid>
        )}
      </Box>
    );
  }

  const renderSection = (
    key: keyof typeof documents,
    title: string,
    uploadKey: string,
  ) => {
    const files = documents[key] || [];
    const hasFiles = files.length > 0;
    if (!hasFiles && role !== "underwriter") return null;
    return (
      <Grid size={{ xs: 12, md: 6 }}>
        <Typography variant="h5" textAlign="left" pb={1}>
          {title}
        </Typography>

        {hasFiles ? (
          files.map((doc, i) => (
            <Box
              key={i}
              sx={{
                background: "#F5EEE9",
                color: "#FF0000",
                borderRadius: 1,
                px: 1.5,
                py: 0.5,
                display: "flex",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Box display="flex" alignItems="center" gap={1} flexGrow={1}>
                <PictureAsPdfIcon />
                <Typography fontSize={14}>{doc.file_name}</Typography>
              </Box>
              {editMode ? (
                <IconButton onClick={() => handleDelete(key, i)}>
                  <DeleteIcon sx={{ color: "red" }} />
                </IconButton>
              ) : (
                <IconButton onClick={() => handlePreview(doc)} size="small">
                  <VisibilityIcon sx={{ color: "#EC6E00" }} />
                </IconButton>
              )}
            </Box>
          ))
        ) : canUpload ? (
          <Box sx={{ textAlign: "center" }}>
            <FileUpload
              inputId={`${uploadKey}-upload`}
              onChange={(e: any) => {
                const file = e.target.files?.[0];
                if (!file) return;
                if (file.size > 2 * 1024 * 1024) {
                  setFileErrors((prev) => ({
                    ...prev,
                    [key]: "File must be less than 2 MB",
                  }));
                  return;
                } else {
                  setFileErrors((prev) => {
                    const updated = { ...prev };
                    delete updated[key];
                    return updated;
                  });
                }
                const newDoc = {
                  file_name: file.name,
                  file_url: URL.createObjectURL(file),
                  isLocal: true,
                };
                setDocuments((prev) => ({
                  ...prev,
                  [key]: [newDoc],
                }));
                addDocument(uploadKey, file);
                if (onFileChosen) onFileChosen(true);
              }}
            />
            {fileErrors[key] && (
              <Typography color="error" fontSize={13} mt={1} textAlign="left">
                {fileErrors[key]}
              </Typography>
            )}
          </Box>
        ) : null}
      </Grid>
    );
  };

  return (
    <Box sx={{ py: 4 }} width="100%">
      <Grid container spacing={3}>
        {renderSection(
          "ASSESSMENT_DOCUMENT",
          "Assessment Document",
          "assessment_document",
        )}
        {renderSection(
          "VERIFICATION_DOCUMENT",
          "Verification Document",
          "verification_document",
        )}
        {renderSection(
          "FRAUD_DOCUMENTATION",
          "Fraud Document",
          "fraud_documentation",
        )}
        {renderSection("CLAIMS_REPORT", "Claims Report", "claims_report")}
        {renderSection("OTHER_DOCUMENT", "Other", "other_document")}
        {comments?.length > 0 && (
          <Grid size={12} mt={3} textAlign="left">
            {comments.map((comment: any, index: number) => (
              <Accordion
                key={index}
                sx={{
                  backgroundColor: "#F7F7F7",
                  boxShadow: "none",
                  borderRadius: "6px",
                  mb: 2,
                  border: "1px solid #E0E0E0",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`panel${index}-content`}
                  id={`panel${index}-header`}
                  sx={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography fontWeight={600} fontSize={14}>
                      {comment.commentedByName}{" "}
                      <Typography
                        component="span"
                        fontWeight={600}
                        color="#EC6E00"
                      >
                        - {comment.role_at_time_of_comment?.toUpperCase()}
                      </Typography>
                    </Typography>
                    <Typography fontSize={12} color="text.secondary">
                      {new Date(comment.created_on).toLocaleTimeString(
                        "en-US",
                        {
                          hour: "numeric",
                          minute: "2-digit",
                        },
                      )}{" "}
                      | {formatDate(comment.created_on)}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography fontSize={15} color="#333" lineHeight={1.6}>
                    {comment.content}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Grid>
        )}
        {isAssigned && claimStatus !== "PAID" && (
          <Grid size={{ xs: 12 }} mt={3}>
            <Typography
              variant="body1"
              fontWeight={600}
              fontSize={18}
              textAlign="left"
            >
              Comment
            </Typography>
            <TextField
              multiline
              rows={4}
              fullWidth
              disabled={isEditable}
              value={reviewComment}
              onChange={onReviewCommentChange}
            />
          </Grid>
        )}
      </Grid>

      <Dialog
        open={openPreview}
        onClose={handleClosePreview}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Document Preview</DialogTitle>
        <DialogContent dividers>
          {selectedDoc ? (
            <object
              data={selectedDoc.file_url}
              type="application/pdf"
              width="100%"
              height="600px"
            >
              <p>
                PDF preview is not supported in this browser.{" "}
                <a
                  href={selectedDoc.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download PDF
                </a>
              </p>
            </object>
          ) : (
            <Typography>Loading...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => handleDownload(selectedDoc)}
            color="primary"
            variant="outlined"
          >
            Download
          </Button>

          <Button
            onClick={handleClosePreview}
            color="secondary"
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
